import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getExerciseTemplates, searchExerciseTemplates } from '../lib/database';
import ExerciseDetailModal from './ExerciseDetailModal';
import BottomNav from './BottomNav';

// Filtri per tipo esercizio (reps/time/hold/cardio; distance confluisce in cardio)
const TYPE_FILTERS = [
  { key: 'all', label: 'Tutti' },
  { key: 'reps', label: 'Ripetizioni' },
  { key: 'time', label: 'Tempo' },
  { key: 'hold', label: 'Isometrici' },
  { key: 'cardio', label: 'Cardio' },
];
const TYPE_BADGE = { reps: 'Reps', time: 'Tempo', hold: 'Hold', cardio: 'Cardio', distance: 'Distanza' };

function ExerciseCatalog() {
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [results, setResults] = useState([]);
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('all');
  const [type, setType] = useState('all');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  // Carica tutto il catalogo una volta
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      const data = await getExerciseTemplates();
      if (mounted) { setAll(data); setResults(data); setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  // Ricerca alias-aware (debounced); sotto 2 caratteri mostra tutto
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults(all); return; }
    const t = setTimeout(async () => {
      const r = await searchExerciseTemplates(q);
      setResults(r);
    }, 300);
    return () => clearTimeout(t);
  }, [query, all]);

  const groups = useMemo(
    () => [...new Set(all.map(e => e.primaryMuscle).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [all]
  );

  const filtered = useMemo(() => {
    return results.filter(e => {
      const t = e.exerciseType || 'reps';
      const okGroup = group === 'all' || e.primaryMuscle === group;
      const okType = type === 'all' || t === type || (type === 'cardio' && t === 'distance');
      return okGroup && okType;
    });
  }, [results, group, type]);

  const chip = (active) => ({
    padding: '6px 12px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap',
    border: '1px solid ' + (active ? '#4f46e5' : '#e5e7eb'),
    background: active ? '#4f46e5' : 'white', color: active ? 'white' : '#374151', cursor: 'pointer',
  });

  return (
    <div className="page-with-bottom-nav" style={{ minHeight: '100vh', background: '#f9fafb', paddingBottom: '96px' }}>
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-700"
          aria-label="Indietro"
        >
          ←
        </button>
        <h1 className="text-xl font-extrabold text-gray-900 flex-1">Catalogo Esercizi</h1>
      </header>

      <div className="px-4 py-4 space-y-3">
        {/* Ricerca */}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome o alias…"
            className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-gray-200 text-base focus:outline-none focus:border-primary-400"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
              aria-label="Pulisci"
            >
              ✕
            </button>
          )}
        </div>

        {/* Filtro tipo */}
        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '2px' }}>
          {TYPE_FILTERS.map(f => (
            <button key={f.key} onClick={() => setType(f.key)} style={chip(type === f.key)}>{f.label}</button>
          ))}
        </div>

        {/* Filtro gruppo muscolare */}
        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '2px' }}>
          <button onClick={() => setGroup('all')} style={chip(group === 'all')}>Tutti i gruppi</button>
          {groups.map(g => (
            <button key={g} onClick={() => setGroup(g)} style={chip(group === g)}>{g}</button>
          ))}
        </div>

        {/* Conteggio */}
        <p className="text-xs text-gray-400">{filtered.length} esercizi</p>

        {/* Lista */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
            <p className="mt-3 text-gray-500 text-sm">Caricamento catalogo…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <div className="text-4xl mb-2">🔎</div>
            <p>Nessun esercizio trovato</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(ex => (
              <button
                key={ex.id}
                onClick={() => setSelected(ex)}
                className="w-full text-left bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100 flex items-center gap-3 active:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 truncate">{ex.name}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {ex.primaryMuscle && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-primary-50 text-primary-700">
                        {ex.primaryMuscle}
                      </span>
                    )}
                    {ex.exerciseType && ex.exerciseType !== 'reps' && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">
                        {TYPE_BADGE[ex.exerciseType] || ex.exerciseType}
                      </span>
                    )}
                    {ex.secondaryMuscles?.length > 0 && (
                      <span className="text-[11px] text-gray-400">
                        +{ex.secondaryMuscles.length} secondari
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-gray-300 text-xl flex-shrink-0">›</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <ExerciseDetailModal
          exercise={{ name: selected.name, exercise_template_id: selected.id }}
          onClose={() => setSelected(null)}
        />
      )}

      <BottomNav />
    </div>
  );
}

export default ExerciseCatalog;
